import React, { useState } from "react";

import Spinner from "@/app/components/Spinner";
import { Button } from "@/app/components/ui/button";
import FitCheck from "@/app/Views/FitCheck/FitCheck";

import { JobDetails } from "../utils/helpers";

type FitCheckState = "idle" | "loading" | "active";

/**
 * Idle-state pitches. One is picked at random per mount, so the card can test which
 * angle moves readers while a single reader keeps one pitch for the whole page view.
 */
const idleVariants = [
	{
		header: "One check before you apply",
		body: "We compare your resume to this job description and show you what is missing.",
	},
	{
		header: "Most resumes are filtered out before a person reads them",
		body: "Make sure yours is not one of them. Let's check it against this posting first.",
	},
	{
		header: "Find the gaps before the employer does",
		body: "We check your resume against every requirement in this posting, then show you what to fix.",
	},
	{
		header: "Do not waste this application",
		body: "You get one chance with this employer. See how your resume matches the role, then fix the weak parts.",
	},
	{
		header: "The same resume will not win every job",
		body: "See how yours reads against this exact role, and what to change for it.",
	},
	{
		header: "Does your resume prove you can do this job?",
		body: "We check your experience against each requirement and show what is not clear yet.",
	},
	{
		header: "Will this resume get you a callback?",
		body: "Check it against this job description and fix what is weak before you apply.",
	},
	{
		header: "Many people will apply. Few will match.",
		body: "See how your resume lines up with this role, and what puts you on the shortlist.",
	},
];

const fitCheckCopy = {
	idle: {
		cta: "Check my fit",
	},
	loading: {
		header: "Checking your fit...",
		body: "Looking for your profile and any previous assessments.",
	},
	active: {
		header: "Check your fit",
		body: "Select your resume to analyze your fit for this role",
		cancel: "Cancel",
	},
};

interface FitCheckSectionProps {
	jobDetails: JobDetails;
	onFitCheckClick: () => boolean | void;
	hideIdleCTA?: boolean;
	startSignal?: number;
	isRunning?: boolean;
}

export function FitCheckSection({
	jobDetails,
	onFitCheckClick,
	hideIdleCTA = false,
	startSignal = 0,
	isRunning = false,
}: FitCheckSectionProps) {
	const [fitCheckState, setFitCheckState] = useState<FitCheckState>("idle");
	// Chosen once per mount: a pitch that changed on every render would be unreadable.
	const [idleCopy] = useState(() => idleVariants[Math.floor(Math.random() * idleVariants.length)]);

	// Listen for a custom event to start FitCheck from elsewhere on the page.
	React.useEffect(() => {
		const handleStart = () => {
			setFitCheckState("loading");
			setTimeout(() => setFitCheckState("active"), 500);
		};
		window.addEventListener("startFitCheck", handleStart);
		return () => window.removeEventListener("startFitCheck", handleStart);
	}, []);

	const handleStartFitCheck = () => {
		setFitCheckState("loading");
		setTimeout(() => setFitCheckState("active"), 500);
	};

	const startedSignalRef = React.useRef(startSignal);
	React.useEffect(() => {
		if (startSignal === startedSignalRef.current) return;
		startedSignalRef.current = startSignal;
		setFitCheckState("active");
	}, [startSignal]);

	const handleIdleCTAClick = () => {
		if (onFitCheckClick() === false) return;
		handleStartFitCheck();
	};

	if (isRunning || fitCheckState === "loading") {
		return (
			<div id="fitcheck-section" className="mb-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
				<div className="flex flex-col items-center justify-center gap-4 px-5 py-14">
					<div className="h-8 w-8">
						<Spinner />
					</div>
					<div className="text-center">
						<h3 className="text-[15px] font-semibold text-gray-900">{fitCheckCopy.loading.header}</h3>
						<p className="mt-0.5 text-sm text-gray-600">{fitCheckCopy.loading.body}</p>
					</div>
				</div>
			</div>
		);
	}

	if (fitCheckState === "active") {
		return (
			<div id="fitcheck-section" className="mb-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
				<FitCheck jobDetails={jobDetails as unknown as Parameters<typeof FitCheck>[0]["jobDetails"]} />
			</div>
		);
	}

	if (hideIdleCTA) {
		return null;
	}

	return (
		<div
			id="fitcheck-section"
			className="bg-linear-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-100 mb-6"
		>
			<div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
				<img src="/images/filling-survey.png" className="w-24 sm:w-32" alt="Check your fit" />
				<div className="flex-1">
					<h3 className="text-lg font-semibold text-gray-900 mb-1">{idleCopy.header}</h3>
					<p className="text-sm text-gray-600 mb-2">{idleCopy.body}</p>
				</div>
				<Button onClick={handleIdleCTAClick} size="sm" variant="outlineSecondary">
					{fitCheckCopy.idle.cta}
				</Button>
			</div>
		</div>
	);
}
